import {createCanvasTimelineDate, getMinutesInWeekdays, getWeekdaysBetweenDates} from '../canvas-timeline/canvas_timeline_util';

export const calculatePersonGroupUtilization = parameterObject => {
	const {
		dateString,
		isInActualMode,
		data,
		startDate: overallStartDate,
		endDate: overallEndDate,
		items,
		todayDate,
		ITEM_TYPE,
		personIds,
		dayDataLight,
	} = parameterObject;
	let minutesAllocated = 0;
	let minutesAvailable = 0;

	const getHolidayCountInPeriod = (holidayCalendarEntryDates, periodStartDate, periodEndDate) => {
		return holidayCalendarEntryDates.filter(date => {
			return date >= periodStartDate && date <= periodEndDate;
		}).length;
	};

	for (const person of data.persons.filter(person => person.active && !person.clientId)) {
		if (!personIds.includes(person.id)) continue;
		const startDate = person.startDate ? Math.max(overallStartDate, person.startDate) : overallStartDate;
		const endDate = person.endDate ? Math.min(overallEndDate, person.endDate) : overallEndDate;
		const personWorkingMinutes = [
			person.monday,
			person.tuesday,
			person.wednesday,
			person.thursday,
			person.friday,
			person.saturday,
			person.sunday,
		];
		const holidayCalendar = person.holidayCalendarId
			? data.holidayCalendars.find(holidayCalendar => person.holidayCalendarId === holidayCalendar.id)
			: data.company.defaultHolidayCalendarId
			? data.holidayCalendars.find(holidayCalendar => data.company.defaultHolidayCalendarId === holidayCalendar.id)
			: data.holidayCalendars.length === 1
			? data.holidayCalendars[0]
			: null;
		const holidayCalendarEntryDatesExcludingDaysOff = [];
		const holidayCalendarEntryDatesIncludingDaysOff = [];
		if (holidayCalendar) {
			for (const holidayCalendarEntry of data.holidayCalendarEntries.filter(
				holidayCalendarEntry => holidayCalendarEntry.holidayCalendarId === holidayCalendar.id
			)) {
				const holidayCalendarEntryDate = createCanvasTimelineDate(
					holidayCalendarEntry.year,
					holidayCalendarEntry.month,
					holidayCalendarEntry.day
				);
				// Ignore out-of-range holidays
				if (!dayDataLight[holidayCalendarEntryDate]) continue;
				if (holidayCalendarEntryDatesIncludingDaysOff.includes(holidayCalendarEntryDate)) continue;
				holidayCalendarEntryDatesIncludingDaysOff.push(holidayCalendarEntryDate);
				if (!personWorkingMinutes[dayDataLight[holidayCalendarEntryDate].isoWeekday - 1]) continue;
				holidayCalendarEntryDatesExcludingDaysOff.push(holidayCalendarEntryDate);
			}
		}
		const weekdaysInPeriod = getWeekdaysBetweenDates(startDate, endDate, dayDataLight);
		for (const holidayCalendarEntryDate of holidayCalendarEntryDatesIncludingDaysOff.filter(
			date => date >= startDate && date <= endDate
		)) {
			weekdaysInPeriod[dayDataLight[holidayCalendarEntryDate].isoWeekday - 1]--;
		}
		minutesAvailable += getMinutesInWeekdays(person, weekdaysInPeriod);

		//////////////////////////////////////////////////////

		//For idleTime the highest value out of daily time registrations or allocations is taken
		const idleTimeDataMap = new Map();

		const taskItemMap = new Map();
		for (const item of items) {
			if (item.startDate > endDate || item.endDate < startDate || item.groupId.split('-')[0] !== person.id) continue;
			if (item.groupId === person.id + '-1') {
				//Ignore idle time time registrations in planning mode
				if (!isInActualMode) continue;
				//Ignore if not within loop period
				if (item.startDate > endDate || item.endDate < startDate) continue;
				//If there is something on this day already, update values, otherwise insert new value
				if (idleTimeDataMap.has(item.startDate)) {
					const currentValues = idleTimeDataMap.get(item.startDate);
					if (item.data.isIdleTimeInternal) {
						currentValues.idleTimeMinutesRegistered += item.data.minutesRegistered;
					} else {
						currentValues.timeOffMinutesRegistered += item.data.minutesRegistered;
					}
				} else {
					idleTimeDataMap.set(item.startDate, {
						idleTimeMinutesAllocated: 0,
						timeOffMinutesAllocated: 0,
						idleTimeMinutesRegistered: item.data.isIdleTimeInternal ? item.data.minutesRegistered : 0,
						timeOffMinutesRegistered: item.data.isIdleTimeInternal ? 0 : item.data.minutesRegistered,
					});
				}
			} else if (item.groupId === person.id + '-2') {
				//Ignore holidays and ignore all non project time allocations in actual mode
				if (item.data.isHoliday) continue;
				//Need to loop through every single day of allocation even if not on day view
				let idleTimeAllocationLoopDate = Math.max(startDate, item.startDate);
				const idleTimeAllocationLoopEndDate = Math.min(endDate, item.endDate);
				while (idleTimeAllocationLoopDate <= idleTimeAllocationLoopEndDate) {
					//Ignore if there is holiday on this day
					if (holidayCalendarEntryDatesIncludingDaysOff.includes(idleTimeAllocationLoopDate)) {
						idleTimeAllocationLoopDate++;
						continue;
					}
					// Disregard past allocations of idle time in actual mode
					if (isInActualMode && idleTimeAllocationLoopDate < todayDate) {
						idleTimeAllocationLoopDate++;
						continue;
					}
					const allocationMinutes = getMinutesInWeekdays(
						item.data.allocation,
						getWeekdaysBetweenDates(idleTimeAllocationLoopDate, idleTimeAllocationLoopDate, dayDataLight)
					);
					//If there is something on this day already, update values, otherwise insert new value
					if (idleTimeDataMap.has(idleTimeAllocationLoopDate)) {
						const currentValues = idleTimeDataMap.get(idleTimeAllocationLoopDate);
						if (item.data.allocation.isIdleTimeInternal) {
							currentValues.idleTimeMinutesAllocated += allocationMinutes;
						} else {
							currentValues.timeOffMinutesAllocated += allocationMinutes;
						}
					} else {
						idleTimeDataMap.set(idleTimeAllocationLoopDate, {
							idleTimeMinutesAllocated: item.data.allocation.isIdleTimeInternal ? allocationMinutes : 0,
							timeOffMinutesAllocated: item.data.allocation.isIdleTimeInternal ? 0 : allocationMinutes,
							idleTimeMinutesRegistered: 0,
							timeOffMinutesRegistered: 0,
						});
					}
					idleTimeAllocationLoopDate++;
				}
			} else if (item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_PROJECT_ALLOCATION) {
				//Ignore project allocations in the past in actual mode
				const calculationStartDate = isInActualMode ? Math.max(todayDate, item.startDate) : item.startDate;
				const weekdaysInPeriod = getWeekdaysBetweenDates(
					Math.max(startDate, calculationStartDate),
					Math.min(endDate, item.endDate),
					dayDataLight
				);
				for (const holidayCalendarEntryDate of holidayCalendarEntryDatesIncludingDaysOff.filter(
					date => date >= Math.max(startDate, calculationStartDate) && date <= Math.min(endDate, item.endDate)
				)) {
					//Remove a weekday if there is one, don't reduce if it is already at 0
					if (weekdaysInPeriod[dayDataLight[holidayCalendarEntryDate].isoWeekday - 1]) {
						weekdaysInPeriod[dayDataLight[holidayCalendarEntryDate].isoWeekday - 1]--;
					}
				}
				const allocationMinutes = getMinutesInWeekdays(item.data.allocation, weekdaysInPeriod);
				minutesAllocated += allocationMinutes;
			} else if (
				item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TIME_REGISTRATION ||
				item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK
			) {
				if (!item.data.projectUseManualAllocations || (isInActualMode && item.data.projectUseManualAllocations)) {
					//Ignore everything from the future for project allocation projects in actual mode
					if (item.data.projectUseManualAllocations && item.startDate >= todayDate) continue;
					//Time registrations are always 1 day only so can just add the value
					if (item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TIME_REGISTRATION && item.data.projectId) {
						if (isInActualMode && !(item.startDate > endDate || item.endDate < startDate)) {
							minutesAllocated += item.data.minutesRegistered;
						}
					} else if (item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TASK) {
						if (item.data.projectUseManualAllocations) continue; //Don't care about tasks in project allocation projects
						if (isInActualMode && item.endDate < todayDate) continue; //Ignore tasks in the past
						if (taskItemMap.has(item.data.task.id)) {
							taskItemMap.get(item.data.task.id).taskItem = item;
						} else {
							taskItemMap.set(item.data.task.id, {taskItem: item, timeRegistrationItems: []});
						}
					} else if (item.itemType === ITEM_TYPE.PEOPLE_SCHEDULING_TIME_REGISTRATION && item.data.taskId) {
						if (taskItemMap.has(item.data.taskId)) {
							taskItemMap.get(item.data.taskId).timeRegistrationItems.push(item);
						} else {
							taskItemMap.set(item.data.taskId, {timeRegistrationItems: [item]});
						}
					}
				}
			}
		}
		if (isInActualMode) {
			const personTimeRegs = data.timeRegistrations.filter(timeReg => timeReg.personId === person.id);
			for (const timeReg of personTimeRegs) {
				const timeRegDate = createCanvasTimelineDate(timeReg.year, timeReg.month, timeReg.day);
				if (timeRegDate >= startDate && timeRegDate <= endDate && timeRegDate < todayDate) {
					minutesAllocated += timeReg.minutesRegistered;
				}
			}
		}
		for (const entry of taskItemMap.entries()) {
			if (isInActualMode) {
				const {taskItem, timeRegistrationItems} = entry[1];
				if (!taskItem) {
					for (const item of timeRegistrationItems) {
						if (item.startDate >= todayDate || item.endDate >= todayDate) continue;
						minutesAllocated += item.data.minutesRegistered;
					}
				} else {
					const {task, taskStartDate, taskEndDate, isHourEstimated, minutesPerEstimationPoint} = taskItem.data;
					let {timeLeft} = task;
					if (!isHourEstimated) {
						timeLeft *= minutesPerEstimationPoint;
					}
					for (const item of timeRegistrationItems) {
						//Increase task timeLeft by all future time registrations for the purpose of heatmap calculations
						if (item.startDate >= todayDate) {
							if (taskEndDate >= todayDate) {
								timeLeft += item.data.minutesRegistered;
							}
						} else {
							//If time registration is within the loop period and before today, add it to the heatmap
							if (item.startDate >= startDate && item.startDate <= endDate) {
								minutesAllocated += item.data.minutesRegistered;
							}
						}
					}
					//Peace out if task has no remaining since it will not affect heatmap regardless of its position
					if (!timeLeft) continue;
					//Check if task is within current loop period (can get to this point if only a time registration outside of task is within looped period)

					if (!(taskStartDate > endDate || taskEndDate < startDate - 1)) {
						const isInThePast = taskEndDate < todayDate;
						//We do not care about tasks in the past
						if (isInThePast) continue;
						//If task is partially in the past and partially in the future, use today as the starting date
						const calculationStartDate = Math.max(todayDate, taskStartDate);
						//How many weekdays are in the task (ex: 1 monday, 1 tuesday, 1 wednesday)
						const taskTotalWeekdays = getWeekdaysBetweenDates(calculationStartDate, taskEndDate, dayDataLight);
						//Weekdays which are within the current loop period
						const taskLoopWeekdays = getWeekdaysBetweenDates(
							Math.max(calculationStartDate, startDate),
							Math.min(taskEndDate, endDate),
							dayDataLight
						);
						//Total length of task in days
						const taskTotalDayCount = taskTotalWeekdays.reduce((total, weekdayCount) => {
							return total + weekdayCount;
						}, 0);
						//Total length of task in days minus days on which the person is supposed to work 0 minutes
						const taskWorkingDayCount =
							taskTotalWeekdays.reduce((total, weekdayCount, index) => {
								return total + (personWorkingMinutes[index] ? weekdayCount : 0);
							}, 0) -
							getHolidayCountInPeriod(
								holidayCalendarEntryDatesExcludingDaysOff,
								calculationStartDate,
								taskEndDate
							);
						//Number of days within current loop period
						const taskLoopTotalDayCount = taskLoopWeekdays.reduce((total, weekdayCount) => {
							return total + weekdayCount;
						}, 0);
						//Number of days within current loop period minus days on which the person is supposed to work 0 minutes
						const taskLoopWorkingDayCount =
							taskLoopWeekdays.reduce((total, weekdayCount, index) => {
								return total + (personWorkingMinutes[index] ? weekdayCount : 0);
							}, 0) -
							getHolidayCountInPeriod(
								holidayCalendarEntryDatesExcludingDaysOff,
								Math.max(calculationStartDate, startDate),
								Math.min(taskEndDate, endDate)
							);
						//If there are any working days during the task's period, spread timeLeft evenly among working days only
						//If there are no working days during the task's period, spread timeLeft evenly among all days
						//Always divide by amount of people assigned
						if (taskWorkingDayCount) {
							const taskMinutesAllocated =
								(timeLeft * taskLoopWorkingDayCount) / taskWorkingDayCount / task.assignedPersons.length;
							minutesAllocated += taskMinutesAllocated;
						} else {
							const taskMinutesAllocated =
								(timeLeft * taskLoopTotalDayCount) / taskTotalDayCount / task.assignedPersons.length;
							minutesAllocated += taskMinutesAllocated;
						}
					}
				}
			} else {
				const {taskItem} = entry[1];
				if (!taskItem) continue;
				const {task, taskStartDate, taskEndDate, isHourEstimated, minutesPerEstimationPoint} = taskItem.data;
				let {estimateForecast} = task;
				//Tasks with no forecast do not contribute anything
				if (!estimateForecast) continue;
				if (!isHourEstimated) {
					estimateForecast *= minutesPerEstimationPoint;
				}
				//Check if task is in current loop period
				if (taskStartDate > endDate || taskEndDate < startDate - 1) continue;

				//If a task is outside the employment period, calculate it as having a shorter duration
				let taskPersonActualStartDate = taskStartDate;
				if (person.startDate && taskStartDate < person.startDate) {
					taskPersonActualStartDate = person.startDate;
				}
				let taskPersonActualEndDate = taskEndDate;
				if (person.endDate && taskEndDate > person.endDate) {
					taskPersonActualEndDate = person.endDate;
				}
				//How many weekdays are in the task (ex: 1 monday, 1 tuesday, 1 wednesday)
				const taskTotalWeekdays = getWeekdaysBetweenDates(
					taskPersonActualStartDate,
					taskPersonActualEndDate,
					dayDataLight
				);
				//Weekdays which are within the current loop period
				const taskLoopWeekdays = getWeekdaysBetweenDates(
					Math.max(taskPersonActualStartDate, startDate),
					Math.min(taskPersonActualEndDate, endDate),
					dayDataLight
				);
				//Total length of task in days
				const taskTotalDayCount = taskTotalWeekdays.reduce((total, weekdayCount) => {
					return total + weekdayCount;
				}, 0);
				//Total length of task in days minus days on which the person is supposed to work 0 minutes
				const taskWorkingDayCount =
					taskTotalWeekdays.reduce((total, weekdayCount, index) => {
						return total + (personWorkingMinutes[index] ? weekdayCount : 0);
					}, 0) -
					getHolidayCountInPeriod(
						holidayCalendarEntryDatesExcludingDaysOff,
						taskPersonActualStartDate,
						taskPersonActualEndDate
					);

				//Number of days within current loop period
				const taskLoopTotalDayCount = taskLoopWeekdays.reduce((total, weekdayCount) => {
					return total + weekdayCount;
				}, 0);
				//Number
				//Number of days within current loop period minus days on which the person is supposed to work 0 minutes
				const taskLoopWorkingDayCount =
					taskLoopWeekdays.reduce((total, weekdayCount, index) => {
						return total + (personWorkingMinutes[index] ? weekdayCount : 0);
					}, 0) -
					getHolidayCountInPeriod(
						holidayCalendarEntryDatesExcludingDaysOff,
						Math.max(taskPersonActualStartDate, startDate),
						Math.min(taskPersonActualEndDate, endDate)
					);
				//If there are any working days during the task's period, spread forecast evenly among working days only
				//If there are no working days during the task's period, spread forecast evenly among all days
				//Always divide by amount of people assigned
				if (taskWorkingDayCount) {
					const taskMinutesAllocated =
						(estimateForecast * taskLoopWorkingDayCount) / taskWorkingDayCount / task.assignedPersons.length;
					minutesAllocated += taskMinutesAllocated;
				} else {
					const taskMinutesAllocated =
						(estimateForecast * taskLoopTotalDayCount) / taskTotalDayCount / task.assignedPersons.length;
					minutesAllocated += taskMinutesAllocated;
				}
			}
		}

		// Looping through idleTimeDataMap to can minutes allocated and to subtract from available time
		for (const day of idleTimeDataMap.keys()) {
			const idleTimeData = idleTimeDataMap.get(day);
			const {idleTimeMinutesRegistered, idleTimeMinutesAllocated, timeOffMinutesRegistered, timeOffMinutesAllocated} =
				idleTimeData;
			// Check if this day is a holiday
			const isHoliday = holidayCalendarEntryDatesExcludingDaysOff.includes(day);
			// Calc the persons minutes available for this day
			const minutesAvailableForDay = isHoliday ? 0 : personWorkingMinutes[dayDataLight[day].isoWeekday - 1];
			const _idleTimeMinutesAllocated =
				isInActualMode && day < todayDate ? idleTimeMinutesRegistered : idleTimeMinutesAllocated;
			const _timeOffMinutesAllocated =
				isInActualMode && day < todayDate ? timeOffMinutesRegistered : timeOffMinutesAllocated;
			minutesAllocated += _idleTimeMinutesAllocated;
			if (day < todayDate && isInActualMode) {
				minutesAllocated += _timeOffMinutesAllocated;
			}
			// External idle time subtracts from available time
			if (day >= todayDate || !isInActualMode) {
				// External idle time subtracts from available time
				minutesAvailable -= Math.min(minutesAvailableForDay, _timeOffMinutesAllocated);
			}
		}

		// Append heatmap data from noAccessHeatMap
		const {noAccessHeatMaps} = data;
		let personHeatmapData = noAccessHeatMaps[person.id];
		if (!personHeatmapData) {
			personHeatmapData = [];
			noAccessHeatMaps[person.id] = personHeatmapData;
		}

		let relevantDataRange;
		for (let i = startDate; i <= endDate; i++) {
			// If we dont have a relevant date range yet, or the current date range's enddate is before our loop date "i"
			if (!relevantDataRange || (relevantDataRange[1] !== null && relevantDataRange[1] < i)) {
				// Find new relevant date range
				relevantDataRange = personHeatmapData.find(
					dataArray =>
						(dataArray[1] >= i && dataArray[0] === null) ||
						(dataArray[1] >= i && dataArray[0] <= i) ||
						(dataArray[1] === null && dataArray[0] <= i)
				);
			}
			if (relevantDataRange) {
				minutesAllocated += relevantDataRange[2];
				// Calc the persons minutes available for this day
				const minutesAvailableForDay = holidayCalendarEntryDatesExcludingDaysOff.includes(i)
					? 0
					: personWorkingMinutes[dayDataLight[i].isoWeekday - 1];
				minutesAvailable -= Math.min(relevantDataRange[3], minutesAvailableForDay); // Subtract timeoff from available. Limit to minutesAvailableForDay.
			}
		}

		// Minutes available can never be below zero
		minutesAvailable = Math.max(minutesAvailable, 0);
	}
	return {dateString, minutesAllocated, minutesAvailable};
};
